/**
 * A situação de um documento emitido, e os textos que a tela escreve em volta dela (#344).
 *
 * Mora fora do componente porque é regra, e não desenho: a mesma classificação vale para a
 * linha da listagem e para cada revisão do diálogo.
 *
 * O servidor traduz a MESMA regra para o `WHERE` do filtro por situação. Os dois lados
 * comparam por DIA do calendário no fuso local — que é o de São Paulo, onde o servidor
 * grava — e o trigésimo dia conta inteiro como "a vencer" nos dois.
 */

import { EMISSION_STATUS_LABELS, type EmissionStatus } from '../../types/document';

/** O corte que o sistema já usa para exame — e que aqui só vale DEPOIS de descartar o vencido. */
const DIAS_PARA_VENCER = 30;

/** Acima disso a contagem em dias deixa de informar e vira ruído: passa a meses. */
const DIAS_ATE_CONTAR_EM_MESES = 60;

const MILISSEGUNDOS_POR_DIA = 24 * 60 * 60 * 1000;

export type CorDaSituacao = 'success' | 'warning' | 'error' | 'default';

export interface SituacaoDaEmissao {
  status: EmissionStatus;
  /** O texto do chip: diz QUANTO falta, não só o rótulo. */
  rotulo: string;
  cor: CorDaSituacao;
}

/** O que a classificação precisa saber. `isLatest` ausente = a linha da revisão vigente. */
export interface EmissaoClassificavel {
  isLatest?: boolean;
  validUntil: string | null;
}

/**
 * O dia do calendário a que a data recebida pertence, à meia-noite LOCAL.
 *
 * Emissão e validade chegam como INSTANTE (`2026-08-28T00:30:00.000Z`), e a validade herda
 * a hora da emissão: um documento emitido às 21h30 em Brasília tem o DIA SEGUINTE escrito
 * no trecho de data do ISO. Ler o trecho é ler o dia em UTC — a coluna "Emitido em"
 * mostrava amanhã, e o corte de 30 dias caía num dia diferente do que o servidor usa.
 * O que vale é o dia local, que é o mesmo fuso em que o servidor grava.
 *
 * O período é `YYYY-MM-DD` puro e não passa por `Date`: `new Date('2026-01-01')` seria
 * lido como meia-noite em UTC e recuaria um dia no Brasil.
 */
function diaDe(valor: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [ano, mes, dia] = valor.split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  const instante = new Date(valor);
  return new Date(instante.getFullYear(), instante.getMonth(), instante.getDate());
}

/** Meia-noite de hoje: a situação é do dia, não do instante em que a tela abriu. */
function inicioDeHoje(): Date {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return hoje;
}

function diasEntre(de: Date, ate: Date): number {
  return Math.round((ate.getTime() - de.getTime()) / MILISSEGUNDOS_POR_DIA);
}

/** Meses cheios entre as duas datas — o mês só conta quando o dia do mês já passou. */
function mesesEntre(de: Date, ate: Date): number {
  const meses = (ate.getFullYear() - de.getFullYear()) * 12 + (ate.getMonth() - de.getMonth());
  return ate.getDate() < de.getDate() ? meses - 1 : meses;
}

function plural(quantidade: number, singular: string, plural_: string): string {
  return `${quantidade} ${quantidade === 1 ? singular : plural_}`;
}

/**
 * As cinco situações, na ordem em que são avaliadas: a primeira que casar vence.
 *
 * A ordem não é detalhe. O corte de 30 dias não tem piso inferior, e aplicá-lo sem descartar
 * antes o que já venceu faria um documento vencido há dois anos aparecer em âmbar como
 * "a vencer".
 */
export function classificarSituacao(
  emissao: EmissaoClassificavel,
  hoje: Date = inicioDeHoje()
): EmissionStatus {
  if (emissao.isLatest === false) return 'substituida';
  if (emissao.validUntil === null) return 'semValidade';

  const validade = diaDe(emissao.validUntil);
  const restam = diasEntre(hoje, validade);

  if (restam < 0) return 'vencido';
  if (restam <= DIAS_PARA_VENCER) return 'aVencer';
  return 'vigente';
}

const COR_POR_STATUS: Record<EmissionStatus, CorDaSituacao> = {
  substituida: 'default',
  vencido: 'error',
  aVencer: 'warning',
  vigente: 'success',
  semValidade: 'default',
};

/** Quanto falta (ou quanto passou), em dias até dois meses e em meses depois disso. */
function quantoFalta(validade: Date, hoje: Date): string {
  const restam = diasEntre(hoje, validade);

  if (restam >= 0) {
    return restam === 0 ? 'Vence hoje' : `Vence em ${plural(restam, 'dia', 'dias')}`;
  }

  const passaram = -restam;
  if (passaram <= DIAS_ATE_CONTAR_EM_MESES) {
    return `Vencido há ${plural(passaram, 'dia', 'dias')}`;
  }
  return `Vencido há ${plural(mesesEntre(validade, hoje), 'mês', 'meses')}`;
}

/** A situação pronta para o chip: estado, texto e cor. */
export function situacaoDaEmissao(
  emissao: EmissaoClassificavel,
  hoje: Date = inicioDeHoje()
): SituacaoDaEmissao {
  const status = classificarSituacao(emissao, hoje);
  const cor = COR_POR_STATUS[status];

  if (status === 'vencido' || status === 'aVencer') {
    return { status, cor, rotulo: quantoFalta(diaDe(emissao.validUntil as string), hoje) };
  }

  return { status, cor, rotulo: EMISSION_STATUS_LABELS[status] };
}

/** Um dia em pt-BR, ou o traço de "não há" — nunca uma data vazia. */
export function formatarDia(iso: string | null): string {
  if (!iso) return '—';
  const dia = diaDe(iso);
  return dia.toLocaleDateString('pt-BR');
}

/**
 * O período do documento. O ano inteiro se escreve como o ano: é assim que as pessoas
 * chamam o PGR de 2026, e "01/01/2026 a 31/12/2026" só faz a coluna trabalhar mais.
 */
export function formatarPeriodo(periodStart: string, periodEnd: string): string {
  const inicio = diaDe(periodStart);
  const fim = diaDe(periodEnd);

  const anoInteiro =
    inicio.getFullYear() === fim.getFullYear() &&
    inicio.getMonth() === 0 &&
    inicio.getDate() === 1 &&
    fim.getMonth() === 11 &&
    fim.getDate() === 31;

  if (anoInteiro) return String(inicio.getFullYear());
  return `${inicio.toLocaleDateString('pt-BR')} a ${fim.toLocaleDateString('pt-BR')}`;
}
